import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshAccess = useCallback(async () => {
    try {
      const r = await api.get("/subscription/status");
      setAccess(r.data);
    } catch { setAccess(null); }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      await refreshAccess();
    } catch {
      setUser(null);
      setAccess(null);
    } finally {
      setLoading(false);
    }
  }, [refreshAccess]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const afterLogin = async (token, u) => {
    localStorage.setItem("suitor_token", token);
    setUser(u);
    await refreshAccess();
    return u;
  };

  const loginEmail = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    return afterLogin(res.data.token, res.data.user);
  };

  const registerEmail = async (email, password, name) => {
    const res = await api.post("/auth/register", { email, password, name });
    return afterLogin(res.data.token, res.data.user);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("suitor_token");
    setUser(null);
    setAccess(null);
  };

  const exchangeSession = async (sessionId) => {
    const res = await api.post("/auth/session", { session_id: sessionId });
    return afterLogin(res.data.token, res.data.user);
  };

  return (
    <AuthContext.Provider value={{ user, access, loading, loginEmail, registerEmail, logout, exchangeSession, checkAuth, refreshAccess }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
